import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData } from "@tanstack/react-query";
import { Box } from "@mui/material";
import {
  Booking,
  InvestmentQuoteNotationEnum,
  InvestmentTypeEnum,
} from "@bn/api/apiv1";
import { useInfiniteListAndSearchAllBookings } from "@api/v1/hooks/useBookings";
import { useRangeSelectControl, useTableSort } from "@hooks";
import { AutoTable, LoadingPlaceHolder, TableSummary } from "@components";
import { BOOKINGS_COMPATIBILITY_VERSION } from "@constants";
import { EditBookingModal } from "./InvestmentTable/InvestmentDetailModal/EditBookingModal";
import { DeleteBookingDialog } from "./InvestmentTable/InvestmentDetailModal/DeleteBookingDialog";
import { TableBookingData } from "./BookingsSingleInvestmentTable";
import { useInvestmentBookingsTableConfig } from "./useInvestmentBookingsTableConfig";

const PER_PAGE = 50;

interface InvestmentProtocolContainerProps {
  investmentId: number;
  investmentQuote: number;
  currentMarketValue: number;
  currencySymbol?: string;
  image?: string | null;
}

const toTableBookingData = (
  bookings: Booking[],
  investmentQuote: number
): TableBookingData[] =>
  bookings.map((booking): TableBookingData => {
    const entryQuote = booking?.entryQuote
      ? Math.abs(booking.entryQuote)
      : null;
    const priceChange =
      booking.type === "dividend" || !entryQuote || entryQuote === 0
        ? null
        : investmentQuote / entryQuote - 1;
    const expenses = (booking.commission ?? 0) + (booking.taxAmount ?? 0);
    const grossGain =
      booking.type === "sell"
        ? (booking.realizedGain?.grossGain ?? null)
        : null;

    return {
      id: booking.id,
      valueDate: booking.valueDate,
      bookingType: booking.type,
      numberOfLots: booking.numberOfLots,
      securityPrice: Math.abs(booking.securityPrice),
      priceChange,
      expenses,
      amount: booking.amount,
      grossGain,
      isExternalID: Boolean(booking.externalId),
      comment: booking.comment,
      currency: booking.currency,
      investmentId: booking.investment.id,
      investmentType: booking.investment.type as InvestmentTypeEnum,
      quoteNotation: booking.investment
        .quoteNotation as InvestmentQuoteNotationEnum,
      dataConsistencyStatus: booking.dataConsistencyStatus,
      isCancelled: booking.cancellationId !== undefined,
    };
  });

export const InvestmentProtocolContainer = ({
  investmentId,
  investmentQuote,
  currencySymbol,
  currentMarketValue,
  image,
}: InvestmentProtocolContainerProps) => {
  const { t } = useTranslation();
  const [startDate] = useState(new Date());

  const rangeData = useRangeSelectControl({
    firstDate: startDate,
    defaultIntervalType: "sinceBeginning",
  });

  const { sort, sortField, sortDirection } = useTableSort({
    field: "valueDate",
    direction: "DESC",
  });

  const [editingBookingId, setEditingBookingId] = useState<number>();
  const [deletingBookingId, setDeletingBookingId] = useState<number>();

  const handleEditBooking = useCallback(
    (bookingId: number) => setEditingBookingId(bookingId),
    []
  );
  const handleDeleteBooking = useCallback(
    (bookingId: number) => setDeletingBookingId(bookingId),
    []
  );

  const config = useInvestmentBookingsTableConfig({
    sortField,
    sortDirection,
    onSort: sort,
    onEditBooking: handleEditBooking,
    onDeleteBooking: handleDeleteBooking,
  });

  const order =
    sortField && sortDirection
      ? [`${sortField} ${sortDirection.toLowerCase()}`]
      : undefined;

  const {
    data,
    isPending,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteListAndSearchAllBookings(
    {
      investmentId: [investmentId],
      minDate: rangeData.range.startDate?.toISOString(),
      maxDate: rangeData.range.endDate.toISOString(),
      enumCompatibilityVersion: BOOKINGS_COMPATIBILITY_VERSION,
      order,
      perPage: PER_PAGE,
    },
    {
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      getNextPageParam: (lastPage) => {
        const page = lastPage.paging?.page ?? 0;
        const pageCount = lastPage.paging?.pageCount ?? 0;
        return page < pageCount ? page + 1 : undefined;
      },
    }
  );

  const rawBookings: Booking[] = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.bookings ?? []),
    [data]
  );

  const bookings = useMemo(
    () => toTableBookingData(rawBookings, investmentQuote),
    [rawBookings, investmentQuote]
  );

  const additionalProps = useMemo(
    () => ({
      currencySymbol,
      onEditBooking: handleEditBooking,
      onDeleteBooking: handleDeleteBooking,
    }),
    [currencySymbol, handleEditBooking, handleDeleteBooking]
  );

  const editingBooking = rawBookings.find(({ id }) => id === editingBookingId);

  if (isPending) {
    return (
      <Box mt={6}>
        <LoadingPlaceHolder />
      </Box>
    );
  }

  return (
    <Box pt={6} pb={6} data-testid="booking-table-container">
      <TableSummary
        summaryValue={currentMarketValue}
        currencySymbol={currencySymbol ?? "EUR"}
        intervalType={rangeData.intervalType}
        range={rangeData.range}
        onSelectInterval={rangeData.selectInterval}
        onSelectCustomInterval={rangeData.customIntervalSelect}
      />
      <Box data-testid="booking-table" mt={1}>
        <AutoTable
          settings={config}
          data={bookings}
          additionalProps={additionalProps}
          isFetching={isFetching && !isFetchingNextPage}
          infiniteScroll={{
            hasMore: Boolean(hasNextPage),
            next: () => {
              fetchNextPage();
            },
            loader: <LoadingPlaceHolder />,
          }}
          showNoData={bookings.length === 0}
          noDataFirstLine={t("investment.protocolTable.noBookings")}
        />
      </Box>

      <EditBookingModal
        booking={editingBooking}
        investmentId={investmentId}
        image={image}
        isOpen={Boolean(editingBooking)}
        onClose={() => setEditingBookingId(undefined)}
        onSuccess={() => setEditingBookingId(undefined)}
      />

      {deletingBookingId !== undefined && (
        <DeleteBookingDialog
          bookingId={deletingBookingId}
          isOpen
          onClose={() => setDeletingBookingId(undefined)}
        />
      )}
    </Box>
  );
};
